import { Button } from '@/components/ui/button';
import { Plus, Rocket, Users, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface QuickStartCardProps {
  preferredAreas: string[];
  onNewDecision: () => void;
  onTeamManagement?: () => void;
}

const AREA_LABELS: Record<string, string> = {
  strategy: 'Strategie',
  team: 'Team',
  product: 'Produkt',
  operations: 'Operatives',
  finance: 'Finanzen',
  marketing: 'Marketing',
  sales: 'Vertrieb',
  technology: 'Technologie',
};

export const QuickStartCard = ({ preferredAreas, onNewDecision, onTeamManagement }: QuickStartCardProps) => {
  return (
    <div className="relative overflow-hidden rounded-xl sm:rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-card via-card to-primary/5 p-4 sm:p-6 transition-all duration-500 hover:border-primary/30 hover:shadow-[0_30px_60px_-20px_hsl(var(--primary)/0.2)]">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-32 sm:w-40 h-32 sm:h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-24 sm:w-32 h-24 sm:h-32 bg-primary/3 rounded-full blur-2xl pointer-events-none" aria-hidden="true" />
      
      {/* Highlight badge - hidden on very small screens */}
      <div className="absolute top-3 sm:top-4 right-3 sm:right-4 hidden xs:block">
        <span className="inline-flex items-center gap-1 sm:gap-1.5 text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
          <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
          Quick Start
        </span>
      </div>

      <div className="relative z-10 space-y-4 sm:space-y-5">
        {/* Header */}
        <div className="flex items-center gap-2.5 sm:gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
            <Rocket className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base sm:text-xl font-semibold text-foreground truncate">Neue Entscheidung starten</h3>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">Starte einen strukturierten Entscheidungsprozess</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 sm:gap-3">
          <Button 
            onClick={onNewDecision}
            size="lg" 
            className="flex-1 h-10 sm:h-12 text-sm sm:text-base font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all"
          >
            <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" />
            <span className="sm:hidden">Neu</span>
            <span className="hidden sm:inline">Neue Entscheidung</span>
          </Button>
          
          {onTeamManagement && (
            <Button 
              onClick={onTeamManagement}
              variant="outline"
              size="lg" 
              className="h-10 sm:h-12 sm:hidden border-primary/20 hover:bg-primary/5 hover:border-primary/30 px-3"
              aria-label="Team verwalten"
            >
              <Users className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
          )}
        </div>

        {/* Preferred Areas - scrollable on mobile */}
        {preferredAreas.length > 0 && (
          <div className="space-y-1.5 sm:space-y-2 pt-2 border-t border-border/50">
            <p className="text-xs sm:text-sm text-muted-foreground">Deine bevorzugten Bereiche:</p>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {preferredAreas.map((area) => (
                <Badge 
                  key={area} 
                  variant="secondary"
                  className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15 transition-colors text-[10px] sm:text-xs"
                >
                  {AREA_LABELS[area] || area}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
